import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useT, type MessageKey } from '../lib/i18n';
import {
  STATE_KEY, labelOf, type FlowDefinition, type RegistryDefinition, type StateDefinition, type StateRole,
  type TransitionDefinition
} from '../lib/registry';
import {
  ANY_Y, STATE_H, STATE_ROLES, STATE_W, actionsInto, actionsOf, actionsOnFlow, arrowsOf, autoLayout, automationsOn,
  boardsOnFlow, connect, disconnect, flowProblems, isAutomation, isEnding, isStart, movesFrom, movesOf, removeState,
  renameState, slug, suggestedTransitions, templatesOnFlow, unique, type ActionDefinition, type Arrow, type FlowProblem,
  type Spot
} from '../lib/flowDesign';
import { ActionEditor, ConditionField, Words, fieldsFor } from './ActionEditor';
import { KindIcon } from './icons';

const COLOURS = ['#6b7280', '#2b5b86', '#1f5f5b', '#7a6aa8', '#8a6100', '#a05a2c', '#2f6b46', '#a4373a'];
const PERMISSIONS = ['move', 'decide', 'update'];

type Selection =
  | { kind: 'flow' }
  | { kind: 'state'; key: string }
  | { kind: 'transition'; index: number }
  | { kind: 'action'; key: string };

/** Where a person moved the states of a flow, kept in their own browser: the definition has no place for it. */
function savedSpots(flowKey: string): Record<string, Spot> {
  try {
    return JSON.parse(window.localStorage.getItem(`geonexora.flow-spots.${flowKey}`) ?? '{}') as Record<string, Spot>;
  } catch {
    return {};
  }
}
function keepSpots(flowKey: string, spots: Record<string, Spot>) {
  try {
    window.localStorage.setItem(`geonexora.flow-spots.${flowKey}`, JSON.stringify(spots));
  } catch {
    // A browser that keeps nothing draws the flow as laid out.
  }
}

/**
 * A flow drawn and edited as what it is (docs/en/40, docs/en/47 §3-§4): its states as boxes, the
 * moves its transitions allow as arrows, the buttons that bring a record into a state and what
 * runs by itself when one enters it - and beside it, whatever is chosen, edited in words.
 *
 * <p>A new move is drawn: from the handle on a state's right to the state it goes to. A move
 * that asks for something - a condition, a reason, a permission - is the arrow chosen and filled
 * in. "Try it" puts a record in a state and shows where it may go from there and which buttons
 * it would be offered; what is wrong is listed as it is drawn, before the server is asked.
 *
 * <p>Everything here is an edit of the working definition: the flow, the actions on what goes
 * through it. Nothing is saved until the screen around it saves.
 */
export function FlowDesigner({ definition, published, flowKey, onChange, onOpenPart, initial }: {
  definition: RegistryDefinition;
  published: RegistryDefinition | null;
  flowKey: string;
  onChange: (next: RegistryDefinition) => void;
  /** Another part of the taxonomy opened: a template that goes through the flow, a board drawn from it. */
  onOpenPart?: (id: string) => void;
  /** What is chosen when the designer opens: an action, for one. */
  initial?: Selection;
}) {
  const { t, locale } = useT();
  const flow = (definition.flows ?? []).find((f) => f.key === flowKey);
  const publishedFlow = (published?.flows ?? []).find((f) => f.key === flowKey);
  const [selection, setSelection] = useState<Selection>(initial ?? { kind: 'flow' });
  const [trying, setTrying] = useState<string | null>(null);
  const [spots, setSpots] = useState<Record<string, Spot>>(() => savedSpots(flowKey));
  const [drawing, setDrawing] = useState<{ from: string; x: number; y: number } | null>(null);
  const [dragging, setDragging] = useState<{ key: string; dx: number; dy: number; moved: boolean } | null>(null);
  const [zoom, setZoom] = useState<number | null>(null);
  const [room, setRoom] = useState(800);
  const canvas = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = canvas.current;
    if (!element) return undefined;
    const observer = new ResizeObserver(([entry]) => setRoom(entry.contentRect.width));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => { setSpots(savedSpots(flowKey)); setSelection(initial ?? { kind: 'flow' }); setTrying(null); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flowKey]);

  const actions = useMemo(() => actionsOnFlow(definition, flowKey), [definition, flowKey]);
  const templates = useMemo(() => templatesOnFlow(definition, flowKey), [definition, flowKey]);
  const boards = useMemo(() => boardsOnFlow(definition, flowKey), [definition, flowKey]);
  const problems = useMemo(() => (flow ? flowProblems(flow, actions, STATE_KEY) : []), [flow, actions]);
  const laid = useMemo(() => (flow ? autoLayout(flow) : {}), [flow]);
  if (!flow) return <div className="empty">{t('taxonomy.gone')}</div>;

  const at = (key: string): Spot => spots[key] ?? laid[key] ?? { x: 24, y: 76 };
  const arrows = arrowsOf(flow, true);
  const open = (flow.transitions ?? []).length === 0;
  const publishedKeys = new Set((publishedFlow?.states ?? []).map((s) => s.key));
  const allowed = trying ? new Set(movesFrom(flow, trying).map((m) => m.to)) : null;
  const width = Math.max(760, ...flow.states.map((s) => at(s.key).x + STATE_W + 60));
  const height = Math.max(360, ...flow.states.map((s) => at(s.key).y + STATE_H + 70));
  // The whole flow in view to begin with; zoomed from there.
  const fit = Math.min(1, Math.max(0.75, (room - 8) / width));
  const scale = zoom ?? fit;

  // ---------------------------------------------------------------- edits

  const setFlow = (next: FlowDefinition, nextActions?: ActionDefinition[]) => {
    const all = actionsOf(definition);
    const replaced = nextActions ? all.map((a) => nextActions.find((n) => n.key === a.key) ?? a) : all;
    onChange({
      ...definition,
      flows: (definition.flows ?? []).map((f) => (f.key === flowKey ? next : f)),
      ...(nextActions ? { actions: replaced } : {})
    } as RegistryDefinition);
  };
  const setState = (key: string, patch: Partial<StateDefinition>) =>
    setFlow({ ...flow, states: flow.states.map((s) => (s.key === key ? { ...s, ...patch } : s)) });
  const setTransition = (index: number, patch: Partial<TransitionDefinition>) =>
    setFlow({ ...flow, transitions: (flow.transitions ?? []).map((x, i) => {
      if (i !== index) return x;
      const next = { ...x, ...patch };
      for (const k of Object.keys(next) as (keyof TransitionDefinition)[]) {
        const v = next[k];
        if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0 && k !== 'from')) delete next[k];
      }
      return next;
    }) });

  function addState() {
    const name = t('tx.flow.newState');
    const key = unique(slug(name) || 'state', flow!.states.map((s) => s.key));
    const lastOpen = [...flow!.states].reverse().find((s) => !isEnding(s));
    const spot = lastOpen ? { x: at(lastOpen.key).x, y: at(lastOpen.key).y + STATE_H + 34 } : { x: 24, y: 76 };
    const next = { ...spots, [key]: spot };
    setSpots(next);
    keepSpots(flowKey, next);
    setFlow({ ...flow!, states: [...flow!.states, { key, labels: { [locale]: name }, colour: COLOURS[1], ...(flow!.states.length === 0 ? { roles: ['start' as StateRole] } : {}) }] });
    setSelection({ kind: 'state', key });
  }

  function addAction(automatic: boolean, state?: string) {
    const target = templates[0]?.key;
    const short = (target ?? 'record').split('.').at(-1);
    const all = actionsOf(definition);
    const key = unique(`action.${short}.${automatic ? 'on-' + (state ?? 'enter') : 'new'}`, all.map((a) => a.key));
    const action: ActionDefinition = automatic
      ? { key, target, labels: { [locale]: t('tx.flow.newAutomation') }, when: { on: 'entered', state: state ?? flow!.states[0]?.key }, effects: [{ kind: 'note', labels: { [locale]: t('tx.flow.newAutomationNote') } }] }
      : { key, target, labels: { [locale]: t('tx.flow.newButton') }, permission: 'move', effects: state ? [{ kind: 'move', state }] : [] };
    onChange({ ...definition, actions: [...all, action] } as RegistryDefinition);
    setSelection({ kind: 'action', key });
  }

  function closeWithSuggestions() {
    setFlow({ ...flow!, transitions: suggestedTransitions(flow!) });
  }

  // ---------------------------------------------------------------- drawing and dragging

  const local = (e: { clientX: number; clientY: number }) => {
    const box = canvas.current!.getBoundingClientRect();
    return { x: (e.clientX - box.left + canvas.current!.scrollLeft) / scale, y: (e.clientY - box.top + canvas.current!.scrollTop) / scale };
  };
  function startDraw(e: ReactPointerEvent, from: string) {
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setDrawing({ from, ...local(e) });
  }
  function startDrag(e: ReactPointerEvent, key: string) {
    if ((e.target as Element).closest('.tx-handle')) return;
    const p = local(e);
    const s = at(key);
    setDragging({ key, dx: p.x - s.x, dy: p.y - s.y, moved: false });
  }
  function move(e: ReactPointerEvent) {
    if (drawing) setDrawing({ ...drawing, ...local(e) });
    if (dragging) {
      const p = local(e);
      const x = Math.max(4, Math.round((p.x - dragging.dx) / 8) * 8);
      const y = Math.max(52, Math.round((p.y - dragging.dy) / 8) * 8);
      const s = at(dragging.key);
      if (Math.abs(s.x - x) + Math.abs(s.y - y) > 2) {
        setSpots((was) => ({ ...was, [dragging.key]: { x, y } }));
        if (!dragging.moved) setDragging({ ...dragging, moved: true });
      }
    }
  }
  function end(e: ReactPointerEvent) {
    if (drawing) {
      const p = local(e);
      const hit = flow!.states.find((s) => {
        const q = at(s.key);
        return p.x >= q.x && p.x <= q.x + STATE_W && p.y >= q.y && p.y <= q.y + STATE_H;
      });
      if (hit && hit.key !== drawing.from) {
        const next = connect(flow!, drawing.from, hit.key);
        setFlow(next);
        const index = (next.transitions ?? []).findIndex((x) => x.to === hit.key && x.from.includes(drawing.from));
        if (index >= 0) setSelection({ kind: 'transition', index });
      }
      setDrawing(null);
    }
    if (dragging) {
      if (dragging.moved) keepSpots(flowKey, spots);
      else if (trying !== null) setTrying(dragging.key);
      else setSelection({ kind: 'state', key: dragging.key });
      setDragging(null);
    }
  }

  // ---------------------------------------------------------------- arrows

  const anyAt = { x: 24, y: ANY_Y };
  const openAt = { x: arrows.some((a) => a.from === '*') ? 170 : 24, y: ANY_Y };
  function path(a: Arrow): { d: string; mx: number; my: number } {
    const pill = a.from === '*' ? anyAt : a.from === '@open' ? openAt : null;
    const from = pill ? { ...pill, w: 130, h: 28 } : { ...at(a.from), w: STATE_W, h: STATE_H };
    const to = { ...at(a.to), w: STATE_W, h: STATE_H };
    const fcx = from.x + from.w / 2; const tcx = to.x + to.w / 2;
    if (pill) {
      // From the pill along the top, then down the gap left of the state and in from its left.
      const x1 = from.x + from.w; const y1 = from.y + from.h / 2;
      const x2 = to.x; const y2 = to.y + to.h / 2 - 8;
      const gap = x2 - 26;
      return { d: `M ${x1} ${y1} L ${Math.max(x1, gap - 14)} ${y1} Q ${gap} ${y1}, ${gap} ${y1 + 14} L ${gap} ${y2 - 14} Q ${gap} ${y2}, ${gap + 14} ${y2} L ${x2} ${y2}`, mx: gap, my: (y1 + y2) / 2 };
    }
    if (Math.abs(fcx - tcx) < STATE_W * 0.6) {
      // One above the other: out of the side, round, and in.
      const down = to.y > from.y;
      const x = from.x + from.w; const bend = 36;
      const y1 = from.y + from.h / 2 + (down ? 8 : -8); const y2 = to.y + to.h / 2 + (down ? -8 : 8);
      return { d: `M ${x} ${y1} C ${x + bend} ${y1}, ${x + bend} ${y2}, ${x} ${y2}`, mx: x + bend * 0.75, my: (y1 + y2) / 2 };
    }
    const forward = tcx > fcx;
    // A move back runs under the boxes, a move forward through the middle, so the two never lie on each other.
    const x1 = forward ? from.x + from.w : from.x; const x2 = forward ? to.x : to.x + to.w;
    const y1 = from.y + from.h / 2 + (forward ? -6 : 10); const y2 = to.y + to.h / 2 + (forward ? -6 : 10);
    const dx = Math.max(30, Math.abs(x2 - x1) / 2);
    const lift = forward ? 0 : 46;
    return {
      d: `M ${x1} ${y1} C ${x1 + (forward ? dx : -dx)} ${y1 + lift}, ${x2 - (forward ? dx : -dx)} ${y2 + lift}, ${x2} ${y2}`,
      mx: (x1 + x2) / 2, my: (y1 + y2) / 2 + lift * 0.75
    };
  }

  const selectedIndex = selection.kind === 'transition' ? selection.index : -1;
  const problemFor = (key: string) => problems.filter((p) => p.state === key);
  const name = (s: StateDefinition) => labelOf(s.labels, locale, s.key);

  return (
    <div className="flow-designer" data-testid="flow-designer">
      <div className="flow-main">
        <div className="flow-head">
          <button className={`flow-title${selection.kind === 'flow' ? ' on' : ''}`} onClick={() => setSelection({ kind: 'flow' })} data-testid="flow-title">
            <KindIcon kind="flows" size={16} /> <strong>{labelOf(flow.labels, locale, flow.key)}</strong> <code>{flow.key}</code>
          </button>
          <span className={`badge ${open ? 'warn' : 'ok'}`} data-testid="flow-openness">{t(open ? 'tx.flow.isOpen' : 'tx.flow.isClosed')}</span>
          <span className="spacer" />
          <div className="flow-tools" role="toolbar" aria-label={t('tx.flow.tools')}>
            <button onClick={addState} data-testid="flow-add-state"><KindIcon kind="state" /> {t('tx.flow.addState')}</button>
            <button onClick={() => addAction(false)} data-testid="flow-add-button"><KindIcon kind="actions" /> {t('tx.flow.addButton')}</button>
            <button onClick={() => addAction(true)} data-testid="flow-add-automation"><KindIcon kind="automation" /> {t('tx.flow.addAutomation')}</button>
            <button className={trying !== null ? 'on-toggle' : ''} data-testid="flow-try"
                    onClick={() => setTrying(trying !== null ? null : (flow.states.find(isStart)?.key ?? flow.states[0]?.key ?? null))}>
              ▶ {t(trying !== null ? 'tx.flow.stopTrying' : 'tx.flow.try')}</button>
            <button className="ghost" onClick={() => { setSpots({}); keepSpots(flowKey, {}); }} title={t('tx.flow.arrangeHint')}>{t('tx.flow.arrange')}</button>
          </div>
        </div>

        {open && (
          <div className="banner info flow-open-note" data-testid="flow-open-note">
            <span>{t('tx.flow.openNote')}</span>
            <button className="link" onClick={closeWithSuggestions} data-testid="flow-suggest">{t('tx.flow.suggest')}</button>
          </div>
        )}
        {trying !== null && (
          <div className="banner flow-trying" data-testid="flow-trying">
            <span>{t('tx.flow.tryingAt', { state: name(flow.states.find((s) => s.key === trying) ?? { key: trying }) })}</span>
            <span className="hint">{t('tx.flow.tryingHint')}</span>
          </div>
        )}

        <div className={`flow-canvas${drawing ? ' drawing' : ''}${trying !== null ? ' trying' : ''}`} ref={canvas}
             onPointerMove={move} onPointerUp={end} onPointerLeave={end} data-testid="flow-canvas">
          <div className="flow-plane-box" style={{ width: width * scale, height: height * scale }}>
          <div className="flow-plane" style={{ width, height, transform: `scale(${scale})` }}>
            <svg className="flow-arrows" width={width} height={height}>
              <defs>
                <marker id="flow-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                  <path d="M 0 0 L 10 5 L 0 10 z" />
                </marker>
                <marker id="flow-arrow-on" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                  <path d="M 0 0 L 10 5 L 0 10 z" className="on" />
                </marker>
              </defs>
              {arrows.map((a, i) => {
                const transition = (flow.transitions ?? [])[a.index];
                const { d, mx, my } = path(a);
                const on = a.index === selectedIndex;
                const live = trying !== null && (a.from === trying || a.from === '*' || (a.from === '@open' && !isEnding(flow.states.find((x) => x.key === trying) ?? { key: '' }))) && allowed?.has(a.to);
                const marks = [transition.guard ? '◆' : '', (transition.requires ?? []).length ? '✎' : '', transition.permission ? '⚿' : ''].join('');
                return (
                  <g key={`${a.from}-${a.to}-${a.index}-${i}`} className={`flow-arrow${on ? ' on' : ''}${live ? ' live' : ''}${a.from === '*' ? ' any' : ''}${trying !== null && !live ? ' faint' : ''}`}
                     onClick={() => setSelection({ kind: 'transition', index: a.index })} data-testid={`flow-arrow-${a.from}-${a.to}`}>
                    <path d={d} className="hit" />
                    <path d={d} markerEnd={on ? 'url(#flow-arrow-on)' : 'url(#flow-arrow)'} />
                    {marks && <text x={mx} y={my - 4} textAnchor="middle" className="flow-arrow-marks">{marks}</text>}
                    <title>{[transition.guard && t('tx.move.onlyIf'), (transition.requires ?? []).length > 0 && t('tx.move.askReason'),
                      transition.permission && t('tx.move.permission')].filter(Boolean).join(' · ') || t('tx.flow.arrowHint')}</title>
                  </g>
                );
              })}
              {drawing && (() => {
                const s = at(drawing.from);
                return <path className="flow-rubber" d={`M ${s.x + STATE_W} ${s.y + STATE_H / 2} L ${drawing.x} ${drawing.y}`} markerEnd="url(#flow-arrow-on)" />;
              })()}
            </svg>

            {arrows.some((a) => a.from === '*') && (
              <div className="flow-any" style={{ left: anyAt.x, top: anyAt.y }}>{t('tx.flow.anyState')}</div>
            )}
            {arrows.some((a) => a.from === '@open') && (
              <div className="flow-any open" style={{ left: openAt.x, top: openAt.y }} title={t('tx.flow.anyOpenHint')}>{t('tx.flow.anyOpen')}</div>
            )}

            {flow.states.map((s) => {
              const p = at(s.key);
              const into = actionsInto(actions, flow, s.key);
              const autos = automationsOn(actions, flow, s.key);
              const bad = problemFor(s.key);
              const classes = ['flow-state'];
              if (selection.kind === 'state' && selection.key === s.key) classes.push('on');
              if (isEnding(s)) classes.push('ending');
              if (trying === s.key) classes.push('here');
              else if (allowed?.has(s.key)) classes.push('reachable');
              else if (trying !== null) classes.push('unreachable');
              if (bad.some((b) => b.severity === 'error')) classes.push('bad');
              else if (bad.length > 0) classes.push('warn');
              return (
                <div key={s.key} className={classes.join(' ')} style={{ left: p.x, top: p.y, width: STATE_W, borderTopColor: s.colour ?? COLOURS[0] }}
                     onPointerDown={(e) => startDrag(e, s.key)} data-testid={`flow-state-${s.key}`}
                     title={bad.map((b) => t(`tx.problem.${b.code}` as MessageKey, { state: name(s), name: b.name ?? '' })).join('\n') || undefined}>
                  <div className="flow-state-top">
                    <span className="flow-state-name">{name(s)}</span>
                    {(s.roles ?? []).map((r) => <span key={r} className={`flow-role flow-role-${r}`}>{t(`types.stateRole.${r}` as MessageKey)}</span>)}
                  </div>
                  <div className="flow-state-bottom">
                    <code>{s.key}</code>
                    {into.length > 0 && <span className="flow-pill" title={into.map((a) => labelOf(a.labels, locale, a.key)).join(', ')}><KindIcon kind="actions" size={11} />{into.length}</span>}
                    {autos.length > 0 && <span className="flow-pill auto" title={autos.map((a) => labelOf(a.labels, locale, a.key)).join(', ')}><KindIcon kind="automation" size={11} />{autos.length}</span>}
                    {bad.length > 0 && <span className="flow-pill bad">!</span>}
                  </div>
                  <span className="tx-handle" role="button" aria-label={t('tx.flow.drawFrom', { state: name(s) })}
                        onPointerDown={(e) => startDraw(e, s.key)} data-testid={`flow-handle-${s.key}`} />
                </div>
              );
            })}
          </div>
          </div>
          <div className="flow-zoom">
            <button className="ghost" onClick={() => setZoom(Math.max(0.4, scale / 1.15))} aria-label={t('taxonomy.zoomOut')}>−</button>
            <button className="ghost" onClick={() => setZoom(null)}>{Math.round(scale * 100)}%</button>
            <button className="ghost" onClick={() => setZoom(Math.min(1.6, scale * 1.15))} aria-label={t('taxonomy.zoomIn')}>+</button>
          </div>
        </div>
        <p className="hint flow-help">{t('tx.flow.help')}</p>

        <ActionStrip actions={actions} flow={flow} selected={selection.kind === 'action' ? selection.key : null}
                     trying={trying} onSelect={(key) => setSelection({ kind: 'action', key })} />

        {problems.length > 0 && (
          <ul className="flow-problems" data-testid="flow-problems">
            {problems.map((p, i) => (
              <li key={i} className={p.severity}>
                <button className="link" onClick={() => {
                  if (p.state) setSelection({ kind: 'state', key: p.state });
                  else if (p.index !== undefined) setSelection({ kind: 'transition', index: p.index });
                  else if (p.action) setSelection({ kind: 'action', key: p.action });
                }}>{problemText(p, flow, locale, t)}</button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <aside className="flow-side" data-testid="flow-side">
        {selection.kind === 'flow' && (
          <FlowPanel flow={flow} templates={templates.map((x) => ({ key: x.key, label: labelOf(x.labels, locale, x.key) }))}
                     boards={boards.map((b) => ({ key: b.key, label: labelOf(b.labels, locale, b.key) }))}
                     onOpenPart={onOpenPart} onChange={(next) => setFlow(next)} onSuggest={closeWithSuggestions}
                     onOpen={() => setFlow({ ...flow, transitions: undefined })} />
        )}
        {selection.kind === 'state' && flow.states.some((s) => s.key === selection.key) && (
          <StatePanel flow={flow} stateKey={selection.key} fixedKey={publishedKeys.has(selection.key)} actions={actions}
                      onChange={(patch) => setState(selection.key, patch)}
                      onRename={(to) => {
                        const renamed = renameState(flow, actionsOf(definition), selection.key, to);
                        setFlow(renamed.flow, renamed.actions);
                        const next = { ...spots, [to]: at(selection.key) };
                        setSpots(next); keepSpots(flowKey, next);
                        setSelection({ kind: 'state', key: to });
                      }}
                      onRemove={() => { setFlow(removeState(flow, selection.key)); setSelection({ kind: 'flow' }); }}
                      onSelectTransition={(index) => setSelection({ kind: 'transition', index })}
                      onSelectAction={(key) => setSelection({ kind: 'action', key })}
                      onAddButton={() => addAction(false, selection.key)}
                      onAddAutomation={() => addAction(true, selection.key)} />
        )}
        {selection.kind === 'transition' && (flow.transitions ?? [])[selection.index] && (
          <TransitionPanel flow={flow} index={selection.index} fields={fieldsFor(definition, templates[0]?.key)}
                           onChange={(patch) => setTransition(selection.index, patch)}
                           onRemove={() => {
                             const rest = (flow.transitions ?? []).filter((_, i) => i !== selection.index);
                             setFlow({ ...flow, transitions: rest.length > 0 ? rest : undefined });
                             setSelection({ kind: 'flow' });
                           }}
                           onDisconnect={(a) => { setFlow(disconnect(flow, a)); setSelection({ kind: 'flow' }); }} />
        )}
        {selection.kind === 'action' && (
          <ActionEditor definition={definition} actionKey={selection.key} onChange={onChange}
                        published={actionsOf(published).some((a) => a.key === selection.key)}
                        onRemove={actionsOf(published).some((a) => a.key === selection.key) ? undefined : () => {
                          onChange({ ...definition, actions: actionsOf(definition).filter((a) => a.key !== selection.key) } as RegistryDefinition);
                          setSelection({ kind: 'flow' });
                        }} />
        )}
      </aside>
    </div>
  );
}

function problemText(p: FlowProblem, flow: FlowDefinition, locale: string,
                     t: (key: MessageKey, vars?: Record<string, string | number>) => string): string {
  const state = flow.states.find((s) => s.key === p.state);
  return t(`tx.problem.${p.code}` as MessageKey, {
    state: state ? labelOf(state.labels, locale as never, state.key) : p.state ?? '', name: p.name ?? '', action: p.action ?? ''
  });
}

/** The buttons and automations on what goes through the flow, as a row of chips under the drawing. */
function ActionStrip({ actions, flow, selected, trying, onSelect }: {
  actions: ActionDefinition[]; flow: FlowDefinition; selected: string | null; trying: string | null; onSelect: (key: string) => void;
}) {
  const { t, locale } = useT();
  if (actions.length === 0) return null;
  const buttons = actions.filter((a) => !isAutomation(a));
  const autos = actions.filter(isAutomation);
  const chip = (a: ActionDefinition) => {
    const goes = movesOf(a, flow);
    const offered = trying === null || goes.length === 0 || movesFrom(flow, trying).some((m) => goes.includes(m.to));
    return (
      <button key={a.key} className={`flow-action-chip ${a.style ?? ''}${selected === a.key ? ' on' : ''}${offered ? '' : ' faint'}`}
              onClick={() => onSelect(a.key)} data-testid={`flow-action-${a.key}`} title={a.key}>
        <KindIcon kind={isAutomation(a) ? 'automation' : 'actions'} size={12} />
        {labelOf(a.labels, locale, a.key)}
        {goes.length > 0 && <span className="flow-goes">→ {goes.map((g) => labelOf(flow.states.find((s) => s.key === g)?.labels, locale, g)).join(', ')}</span>}
      </button>
    );
  };
  return (
    <div className="flow-actions" data-testid="flow-actions">
      <div className="flow-actions-row"><span className="hint">{t('tx.flow.buttons', { count: buttons.length })}</span>{buttons.map(chip)}</div>
      {autos.length > 0 && <div className="flow-actions-row"><span className="hint">{t('tx.flow.automations', { count: autos.length })}</span>{autos.map(chip)}</div>}
    </div>
  );
}

function FlowPanel({ flow, templates, boards, onOpenPart, onChange, onSuggest, onOpen }: {
  flow: FlowDefinition; templates: { key: string; label: string }[]; boards: { key: string; label: string }[];
  onOpenPart?: (id: string) => void; onChange: (next: FlowDefinition) => void; onSuggest: () => void; onOpen: () => void;
}) {
  const { t } = useT();
  const open = (flow.transitions ?? []).length === 0;
  return (
    <div className="flow-panel" data-testid="flow-panel">
      <h3>{t('tx.flow.aboutFlow')}</h3>
      <Words label={t('tx.flow.name')} value={flow.labels} testid="flow-name" onChange={(labels) => onChange({ ...flow, labels })} />
      <h4>{t('tx.flow.goesThrough')}</h4>
      {templates.length === 0 ? <p className="hint">{t('tx.flow.noTemplates')}</p> : (
        <div className="chips">{templates.map((x) => (
          <button key={x.key} className="chip" onClick={() => onOpenPart?.(`template:${x.key}`)}>{x.label}</button>
        ))}</div>
      )}
      {boards.length > 0 && (
        <>
          <h4>{t('tx.flow.boards')}</h4>
          <div className="chips">{boards.map((b) => (
            <button key={b.key} className="chip" onClick={() => onOpenPart?.(`boards:${b.key}`)}>{b.label}</button>
          ))}</div>
        </>
      )}
      <h4>{t('tx.flow.moves')}</h4>
      <p className="hint">{t(open ? 'tx.flow.openExplained' : 'tx.flow.closedExplained', { count: (flow.transitions ?? []).length })}</p>
      {open
        ? <button className="primary" onClick={onSuggest}>{t('tx.flow.suggest')}</button>
        : <button className="ghost" onClick={onOpen}>{t('tx.flow.reopen')}</button>}
      <h4>{t('tx.flow.howTo')}</h4>
      <ol className="flow-howto">
        <li>{t('tx.flow.howTo1')}</li>
        <li>{t('tx.flow.howTo2')}</li>
        <li>{t('tx.flow.howTo3')}</li>
        <li>{t('tx.flow.howTo4')}</li>
      </ol>
    </div>
  );
}

function StatePanel({ flow, stateKey, fixedKey, actions, onChange, onRename, onRemove, onSelectTransition, onSelectAction, onAddButton, onAddAutomation }: {
  flow: FlowDefinition; stateKey: string; fixedKey: boolean; actions: ActionDefinition[];
  onChange: (patch: Partial<StateDefinition>) => void; onRename: (to: string) => void; onRemove: () => void;
  onSelectTransition: (index: number) => void; onSelectAction: (key: string) => void;
  onAddButton: () => void; onAddAutomation: () => void;
}) {
  const { t, locale } = useT();
  const state = flow.states.find((s) => s.key === stateKey)!;
  const [key, setKey] = useState(state.key);
  useEffect(() => setKey(state.key), [state.key]);
  const taken = flow.states.some((s) => s.key === key && s.key !== state.key);
  const keyOk = STATE_KEY.test(key) && !taken;
  const roles = state.roles ?? [];
  const out = movesFrom(flow, stateKey);
  const incoming = flow.states.filter((s) => s.key !== stateKey && movesFrom(flow, s.key).some((m) => m.to === stateKey));
  const into = actionsInto(actions, flow, stateKey);
  const autos = automationsOn(actions, flow, stateKey);
  const name = (k: string) => labelOf(flow.states.find((s) => s.key === k)?.labels, locale, k);
  return (
    <div className="flow-panel" data-testid="state-panel">
      <h3><span className="flow-swatch" style={{ background: state.colour ?? COLOURS[0] }} /> {name(stateKey)}</h3>
      <Words label={t('tx.state.name')} value={state.labels} testid="state-name" onChange={(labels) => onChange({ labels })} />
      <label className="field"><span>{t('tx.state.key')}</span>
        <div className="tx-key-row">
          <input value={key} readOnly={fixedKey} spellCheck={false} className="mono" data-testid="state-key"
                 onChange={(e) => setKey(e.target.value.trim())} title={fixedKey ? t('types.keyFixed') : undefined} />
          {!fixedKey && key !== state.key && <button disabled={!keyOk} onClick={() => onRename(key)}>{t('tx.state.rename')}</button>}
        </div>
        {fixedKey && <span className="hint">{t('tx.state.keyFixed')}</span>}
        {!keyOk && key !== state.key && <span className="hint problem">{t('tx.state.keyBad')}</span>}
      </label>
      <div className="field"><span>{t('tx.state.colour')}</span>
        <div className="flow-colours">
          {COLOURS.map((c) => (
            <button key={c} className={`flow-colour${(state.colour ?? COLOURS[0]) === c ? ' on' : ''}`} style={{ background: c }}
                    aria-label={c} onClick={() => onChange({ colour: c })} />
          ))}
        </div>
      </div>
      <div className="field"><span>{t('tx.state.means')}</span>
        <div className="chips">
          {STATE_ROLES.map((r) => (
            <button key={r} className={`chip${roles.includes(r) ? ' on' : ''}`} data-testid={`state-role-${r}`}
                    onClick={() => onChange({ roles: roles.includes(r) ? roles.filter((x) => x !== r) : [...roles, r] })}>
              {t(`types.stateRole.${r}` as MessageKey)}</button>
          ))}
        </div>
        <span className="hint">{t('tx.state.meansHint')}</span>
      </div>

      <h4>{t('tx.state.from')}</h4>
      {incoming.length === 0 ? <p className="hint">{t(isStart(state) ? 'tx.state.startHere' : 'tx.state.noIncoming')}</p> : (
        <div className="chips">{incoming.map((s) => {
          const index = movesFrom(flow, s.key).find((m) => m.to === stateKey)?.index;
          return <button key={s.key} className="chip" onClick={() => index !== undefined && onSelectTransition(index)}>{name(s.key)} →</button>;
        })}</div>
      )}
      <h4>{t('tx.state.to')}</h4>
      {out.length === 0 ? <p className={`hint${isEnding(state) ? '' : ' problem'}`}>{t(isEnding(state) ? 'tx.state.ends' : 'tx.state.deadEnd')}</p> : (
        <div className="chips">{out.map((m) => (
          <button key={m.to} className="chip" onClick={() => m.index !== undefined && onSelectTransition(m.index)}>→ {name(m.to)}</button>
        ))}</div>
      )}

      <h4>{t('tx.state.buttonsInto')}</h4>
      {into.length === 0 && <p className="hint">{t('tx.state.noButtons')}</p>}
      <div className="chips">{into.map((a) => (
        <button key={a.key} className={`chip flow-action-chip ${a.style ?? ''}`} onClick={() => onSelectAction(a.key)}>{labelOf(a.labels, locale, a.key)}</button>
      ))}</div>
      <button className="tx-add" onClick={onAddButton} data-testid="state-add-button">+ {t('tx.state.addButton', { state: name(stateKey) })}</button>

      <h4>{t('tx.state.onEnter')}</h4>
      {autos.length === 0 && <p className="hint">{t('tx.state.noAutomations')}</p>}
      <div className="chips">{autos.map((a) => (
        <button key={a.key} className="chip flow-action-chip" onClick={() => onSelectAction(a.key)}><KindIcon kind="automation" size={12} /> {labelOf(a.labels, locale, a.key)}</button>
      ))}</div>
      <button className="tx-add" onClick={onAddAutomation} data-testid="state-add-automation">+ {t('tx.state.addAutomation')}</button>

      <div className="tx-danger-zone">
        <button className="ghost danger-text" disabled={fixedKey} title={fixedKey ? t('tx.state.removeFixed') : undefined}
                onClick={onRemove} data-testid="state-remove">{t('tx.state.remove')}</button>
      </div>
    </div>
  );
}

function TransitionPanel({ flow, index, fields, onChange, onRemove, onDisconnect }: {
  flow: FlowDefinition; index: number; fields: { key: string; label: string }[];
  onChange: (patch: Partial<TransitionDefinition>) => void; onRemove: () => void; onDisconnect: (a: Arrow) => void;
}) {
  const { t, locale } = useT();
  const x = (flow.transitions ?? [])[index];
  const [guarded, setGuarded] = useState(Boolean(x.guard));
  useEffect(() => setGuarded(Boolean(x.guard)), [index, x.guard]);
  const name = (k: string) => (k === '*' ? t('tx.flow.anyState')
    : k.startsWith('@') ? t('tx.anyRole', { role: t(`types.stateRole.${k.slice(1)}` as MessageKey) })
      : labelOf(flow.states.find((s) => s.key === k)?.labels, locale, k));
  const sources = ['*', ...flow.states.map((s) => s.key), ...STATE_ROLES.map((r) => `@${r}`)];
  const toggle = (k: string) => {
    const from = x.from.includes(k) ? x.from.filter((f) => f !== k) : [...x.from.filter((f) => !(k === '*' || f === '*')), k];
    if (from.length > 0) onChange({ from });
  };
  return (
    <div className="flow-panel" data-testid="transition-panel">
      <h3>{t('tx.move.title', { from: x.from.map(name).join(', '), to: name(x.to) })}</h3>
      <div className="field"><span>{t('tx.move.from')}</span>
        <div className="chips">{sources.map((k) => (
          <button key={k} className={`chip${x.from.includes(k) ? ' on' : ''}`} data-testid={`move-from-${k}`} onClick={() => toggle(k)}>{name(k)}</button>
        ))}</div>
      </div>
      <label className="field"><span>{t('tx.move.to')}</span>
        <select value={x.to} data-testid="move-to" onChange={(e) => onChange({ to: e.target.value })}>
          {flow.states.map((s) => <option key={s.key} value={s.key}>{name(s.key)}</option>)}
          {STATE_ROLES.map((r) => <option key={r} value={`@${r}`}>{name(`@${r}`)}</option>)}
        </select></label>

      <h4>{t('tx.move.rules')}</h4>
      <label className="field-check"><input type="checkbox" checked={guarded} data-testid="move-guarded"
             onChange={(e) => { setGuarded(e.target.checked); if (!e.target.checked) onChange({ guard: undefined, refusal: undefined }); }} />
        {t('tx.move.onlyIf')}</label>
      {guarded && (
        <>
          <ConditionField value={x.guard ?? ''} fields={fields} parameters={x.requires ?? []} testid="move-guard"
                          onChange={(guard) => onChange({ guard })} />
          <label className="field"><span>{t('tx.move.refusal')}</span>
            <input value={x.refusal?.[locale] ?? ''} placeholder={labelOf(x.refusal, locale, '')} data-testid="move-refusal"
                   onChange={(e) => onChange({ refusal: { ...(x.refusal ?? {}), [locale]: e.target.value } })} /></label>
        </>
      )}
      <label className="field-check"><input type="checkbox" checked={(x.requires ?? []).includes('reason')} data-testid="move-reason"
             onChange={(e) => onChange({ requires: e.target.checked ? [...(x.requires ?? []), 'reason'] : (x.requires ?? []).filter((r) => r !== 'reason') })} />
        {t('tx.move.askReason')}</label>
      <label className="field"><span>{t('tx.move.permission')}</span>
        <select value={x.permission ?? ''} data-testid="move-permission" onChange={(e) => onChange({ permission: e.target.value || undefined })}>
          <option value="">{t('tx.move.anyone')}</option>
          {PERMISSIONS.map((p) => <option key={p} value={p}>{t(`tx.permission.${p}` as MessageKey)}</option>)}
          {x.permission && !PERMISSIONS.includes(x.permission) && <option value={x.permission}>{x.permission}</option>}
        </select></label>

      <div className="tx-danger-zone">
        {x.from.length > 1 && x.from.filter((f) => f !== '*').map((f) => (
          <button key={f} className="ghost" onClick={() => onDisconnect({ from: f, to: x.to, index })}>{t('tx.move.dropFrom', { state: name(f) })}</button>
        ))}
        <button className="ghost danger-text" onClick={onRemove} data-testid="move-remove">{t('tx.move.remove')}</button>
      </div>
    </div>
  );
}
